import * as yup from "yup";

export const RECEIPT_STATUS = {
  ACCEPTED: "accepted",
  NEEDS_CONFIRMATION: "needs_confirmation",
  AMOUNT_REQUIRED: "amount_required",
  REJECTED: "rejected",
};

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

/** Converts Persian/Arabic digits to ASCII and strips separators and spaces. */
export function toAsciiDigits(value) {
  return String(value ?? "")
    .replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)))
    .replace(/[،,٬\s-]/g, "");
}

/** Normalizes an IBAN to the stored `IR` + 24 digits shape. */
export function normalizeSheba(value) {
  const cleaned = toAsciiDigits(value).toUpperCase();
  if (!cleaned) return "";
  return cleaned.startsWith("IR") ? cleaned : `IR${cleaned}`;
}

export function parseAmount(value) {
  const n = Number(toAsciiDigits(value));
  return Number.isFinite(n) ? n : 0;
}

export function rialToToman(rial) {
  return Math.floor(Number(rial || 0) / 10);
}

export function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("fa-IR");
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Shapes a bot-extracted account (or saved row) into the editable form state. */
export function toAccountForm(source = {}) {
  return {
    accountHolderName: source.accountHolderName ?? "",
    accountHolderFamilyName: source.accountHolderFamilyName ?? "",
    cardNumber: source.cardNumber ?? "",
    shebaNumber: source.shebaNumber ?? "",
    accountNumber: source.accountNumber ?? "",
    bankName: source.bankName ?? "",
    infoText: source.infoText ?? "",
    amount: source.amount ? Number(source.amount).toLocaleString("fa-IR") : "",
  };
}

/** Same required triad as the bot: family name + amount + one of card/IBAN/account. */
export const bankAccountSchema = yup.object({
  accountHolderName: yup.string().trim().max(150, "نام بیش از حد طولانی است"),
  accountHolderFamilyName: yup
    .string()
    .trim()
    .required("نام خانوادگی صاحب حساب الزامی است")
    .max(150, "نام خانوادگی بیش از حد طولانی است"),
  cardNumber: yup
    .string()
    .transform((v) => toAsciiDigits(v))
    .test("card", "شماره کارت باید ۱۶ رقم باشد", (v) => !v || /^\d{16}$/.test(v)),
  shebaNumber: yup
    .string()
    .transform((v) => normalizeSheba(v))
    .test("sheba", "شماره شبا باید با IR و ۲۴ رقم باشد", (v) => !v || /^IR\d{24}$/.test(v)),
  accountNumber: yup
    .string()
    .transform((v) => toAsciiDigits(v))
    .test("account", "شماره حساب معتبر نیست", (v) => !v || /^[\d.]{5,30}$/.test(v)),
  bankName: yup.string().trim().max(100, "نام بانک بیش از حد طولانی است"),
  infoText: yup.string().trim().max(1000, "متن بیش از حد طولانی است"),
  amount: yup
    .number()
    .transform((_, original) => parseAmount(original))
    .typeError("مبلغ معتبر نیست")
    .moreThan(0, "مبلغ این حساب (تومان) الزامی است"),
}).test(
  "account-identifier",
  "حداقل یکی از شماره کارت، شبا یا حساب الزامی است",
  (value) => Boolean(value?.cardNumber || value?.shebaNumber || value?.accountNumber)
);

/**
 * Validates the account form and returns either `{ values }` ready for the API
 * or `{ errors }` keyed by field (`_form` for cross-field errors).
 */
export async function validateBankAccount(form) {
  try {
    const values = await bankAccountSchema.validate(form, { abortEarly: false, stripUnknown: true });
    return { values };
  } catch (err) {
    const errors = {};
    for (const issue of err.inner?.length ? err.inner : [err]) {
      const key = issue.path || "_form";
      if (!errors[key]) errors[key] = issue.message;
    }
    return { errors };
  }
}

/** Remaining-time label for a send-log deadline. */
export function deadlineLabel(deadline, now = new Date()) {
  const end = new Date(deadline);
  if (Number.isNaN(end.getTime())) return "";
  const diffMs = end.getTime() - now.getTime();
  if (diffMs <= 0) return "مهلت ارسال فیش تمام شده است";
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours >= 24) return `مهلت تا ${formatDateTime(end)}`;
  if (hours > 0) return `${hours.toLocaleString("fa-IR")} ساعت و ${(minutes % 60).toLocaleString("fa-IR")} دقیقه تا پایان مهلت`;
  return `${minutes.toLocaleString("fa-IR")} دقیقه تا پایان مهلت`;
}
